import { Box, Grid, Tooltip, Typography, styled } from '@mui/material';
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import getStatusOptions from '../../../../constants/statusOptions';
import { RootState } from '../../../../utils/store';

interface CustomLinearProps {
  place?: string;
  status?: string;
}

const BorderLinearProgress = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== 'place' && prop !== 'status',
})(({ place, status }: CustomLinearProps) => {
  const setBg = (defaultColor: string) => {
    let bgcolor = defaultColor;
    const statusOptions = getStatusOptions();
    if (status?.includes('Closed')) bgcolor = '#00CECF';
    if (status === statusOptions[5].name) bgcolor = '#B0B0B0';

    return bgcolor;
  };

  if (place === 'head')
    return {
      height: 10,
      borderTopLeftRadius: 5,
      borderBottomLeftRadius: 5,
      [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: '#F0F0F0',
      },
      [`& .${linearProgressClasses.bar}`]: {
        backgroundColor: setBg('#4E7AFF'),
      },
    };

  if (place === 'body')
    return {
      height: 10,
      [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: '#F0F0F0',
      },
      [`& .${linearProgressClasses.bar}`]: {
        backgroundColor: setBg('#F49D0C'),
      },
    };

  return {
    height: 10,
    borderTopRightRadius: 5,
    borderBottomRightRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: '#F0F0F0',
    },
    [`& .${linearProgressClasses.bar}`]: {
      backgroundColor: setBg('#00CECF'),
    },
  };
});

const ReportProgress = () => {
  const { status } = useSelector((state: RootState) => state.reportData.report);
  const statusOptions = getStatusOptions();
  const statusName = statusOptions.find((option) => option.id === status)?.name;
  let head = -1;
  let body = -1;
  let tail = -1;
  let progress = 0;

  const getProgress = () => {
    if (statusName === statusOptions[1].name) {
      head = 100;
      progress = 33;
    }
    if (statusName === statusOptions[2].name) {
      head = 100;
      body = 100;
      progress = 66;
    }
    if (
      statusName?.includes('Closed') ||
      statusName === statusOptions[5].name
    ) {
      head = 100;
      body = 100;
      tail = 100;
      progress = 100;
    }

    return {
      head: head,
      body: body,
      tail: tail,
      progress: progress,
      statusProgressValue: (statusProgress: string | undefined) => {
        let progressValue = 0;

        if (statusProgress === statusOptions[1].name) progressValue = 33;

        if (statusProgress === statusOptions[2].name) progressValue = 66;

        if (
          statusProgress?.includes('Closed') ||
          statusProgress === statusOptions[5].name
        )
          progressValue = 100;

        return progressValue;
      },
    };
  };

  return (
    <Box sx={{ my: 1, px: 2 }}>
      <Typography variant='body2' color='#4F4F4F'>
        <FormattedMessage defaultMessage='Status' />
      </Typography>
      <Tooltip
        title={`${getProgress().statusProgressValue(
          statusName,
        )}%: ${statusName}`}
        placement='top'
        disableHoverListener={
          getProgress().progress !== 100 && getProgress().progress !== 0
        }
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [-110, -5],
                },
              },
            ],
          },
        }}
      >
        <Grid
          container
          direction='row'
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Grid
            item
            xs={3.23}
            pr={
              getProgress().progress >= 33 && getProgress().progress < 100
                ? 0.5
                : 0
            }
          >
            <Tooltip
              title={`${getProgress().statusProgressValue(
                statusOptions[1].name,
              )}%: ${statusOptions[1].name}`}
              placement='top'
              disableHoverListener={
                getProgress().progress === 0 || getProgress().progress === 100
              }
            >
              <BorderLinearProgress
                place='head'
                status={statusName}
                variant='determinate'
                value={getProgress().head}
              />
            </Tooltip>
          </Grid>
          <Grid
            item
            xs={3.23}
            pr={
              getProgress().progress >= 66 && getProgress().progress < 100
                ? 0.5
                : 0
            }
          >
            <Tooltip
              title={`${getProgress().statusProgressValue(
                statusOptions[2].name,
              )}%: ${statusOptions[2].name}`}
              placement='top'
              disableHoverListener={
                getProgress().progress === 0 ||
                getProgress().progress === 33 ||
                getProgress().progress === 100
              }
            >
              <BorderLinearProgress
                place='body'
                status={statusName}
                variant='determinate'
                value={getProgress().body}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={3.23}>
            <BorderLinearProgress
              place='tail'
              status={statusName}
              variant='determinate'
              value={getProgress().tail}
            />
          </Grid>
          <Grid item xs={2.21} pl={1}>
            <Typography variant='body2' color='text.secondary'>{`${
              getProgress().progress
            }% Completed`}</Typography>
          </Grid>
        </Grid>
      </Tooltip>
    </Box>
  );
};

export default ReportProgress;
